/**
 * Convert a measurement value between unit systems (imperial <-> metric).
 *
 * @param {number} value - Numeric value to convert
 * @param {string} unit - Unit of the measurement (e.g., 'ft', 'm', '°C')
 * @param {boolean} [toOpposite=true] - If true, convert to the opposite unit system
 * @returns {{value:number,unit:string}|null} Converted value and unit, or null if input invalid
 */
export function convertMeasurement(value, unit, toOpposite = true) {
  let result = null;
  if (!toOpposite) {
    result = { value, unit };
  } else if (value == null || !isFinite(Number(value))) {
    result = null;
  } else {
    const v = Number(value);
    const u = (unit || '').toString().trim().toLowerCase();

    // use switch(true) and test regexes directly
    switch (true) {
      case /(deg\s*c|celsius|°c|c$)/i.test(u):
        // Convert Celsius to Fahrenheit: F = C * 9/5 + 32
        // Target unit: °F
        result = { value: roundToHundredths((v * 9) / 5 + 32), unit: '°F' };
        break;
      case /(deg\s*f|fahrenheit|°f|f$)/i.test(u):
        // Convert Fahrenheit to Celsius: C = (F - 32) * 5/9
        // Target unit: °C
        result = { value: roundToHundredths(((v - 32) * 5) / 9), unit: '°C' };
        break;
      case /^(ft|feet?)$/.test(u):
        // Convert feet to meters: m = ft * 0.3048
        // Target unit: m
        result = { value: roundToHundredths(v * 0.3048), unit: 'm' };
        break;
      case /^(in|inch|inches)$/.test(u):
        // Convert inches to centimeters: cm = in * 2.54
        // Target unit: cm
        result = { value: roundToHundredths(v * 2.54), unit: 'cm' };
        break;
      case /^(yd|yard|yards)$/.test(u):
        // Convert yards to meters: m = yd * 0.9144
        // Target unit: m
        result = { value: roundToHundredths(v * 0.9144), unit: 'm' };
        break;
      case /(mi|mile|miles)$/.test(u):
        // Convert miles to kilometers: km = mi * 1.609344
        // Target unit: km
        result = { value: roundToHundredths(v * 1.609344), unit: 'km' };
        break;
      case /^(m|meter|metre|meters|metres)$/.test(u):
        // Convert meters to feet: ft = m / 0.3048
        // Target unit: ft
        result = { value: roundToHundredths(v / 0.3048), unit: 'ft' };
        break;
      case /^(cm|centimeter|centimetre|centimeters|centimetres)$/.test(u):
        // Convert centimeters to inches: in = cm / 2.54
        // Target unit: in
        result = { value: roundToHundredths(v / 2.54), unit: 'in' };
        break;
      case /^(km|kilometer|kilometre|kilometers|kilometres)$/.test(u):
        // Convert kilometers to miles: mi = km / 1.609344
        // Target unit: mi
        result = { value: roundToHundredths(v / 1.609344), unit: 'mi' };
        break;
      case /^(psi)$/.test(u):
        // Convert PSI to kilopascals: kPa = psi * 6.89475729
        // Target unit: kPa
        result = { value: roundToHundredths(v * 6.89475729), unit: 'kPa' };
        break;
      case /^(kpa|kilopascal|kilopascals)$/.test(u):
        // Convert kilopascals to PSI: psi = kPa / 6.89475729
        // Target unit: psi
        result = { value: roundToHundredths(v / 6.89475729), unit: 'psi' };
        break;
      default:
        result = { value: v, unit };
        break;
    }
  }

  return result;
}

/**
 * Readable map of basic unit -> converted unit (for documentation or UI display).
 * E.g., "ft" -> "m", "celsius" -> "°F"
 */
export const unitConversionNotes = {
  ft: 'm',
  feet: 'm',
  in: 'cm',
  inch: 'cm',
  inches: 'cm',
  yd: 'm',
  yard: 'm',
  yards: 'm',
  mi: 'km',
  mile: 'km',
  miles: 'km',
  m: 'ft',
  meter: 'ft',
  metre: 'ft',
  meters: 'ft',
  metres: 'ft',
  cm: 'in',
  centimeter: 'in',
  centimetre: 'in',
  centimeters: 'in',
  centimetres: 'in',
  km: 'mi',
  kilometer: 'mi',
  kilometre: 'mi',
  kilometers: 'mi',
  kilometres: 'mi',
  c: '°F',
  'deg c': '°F',
  celsius: '°F',
  f: '°C',
  'deg f': '°C',
  fahrenheit: '°C',
  psi: 'kPa',
  kpa: 'psi',
  kilopascal: 'psi',
};

export function getConversionTarget(unit) {
  if (!unit) return null;
  const key = unit.toString().trim().toLowerCase();
  return unitConversionNotes[key] || null;
}

export function formatTemp(unit) {
  if (!unit && unit !== 0) return unit;
  const u = unit.toString().trim();
  const lower = u.toLowerCase();
  if (/^deg\s*c$/.test(lower) || /celsius/i.test(lower) || /^degc$/i.test(lower)) return '°C';
  if (/^deg\s*f$/.test(lower) || /fahrenheit/i.test(lower) || /^degf$/i.test(lower)) return '°F';
  return unit;
}

/**
 * Normalize and return values and units for the purpose of USGS standardization.
 *
 * @param {number} value - Numeric value to convert
 * @param {string} unit - Unit of the measurement (e.g., 'ft', 'm', '°C')
 * @returns {{value:number,unit:string,altValue:number,altUnit:string}|null} Converted value and unit, or null if input invalid
 */
export function USGSMeasureNormalizer(value, unit) {
  if (value == null || !isFinite(Number(value))) return null;
  if (unit == null) return { value: Number(value), unit: null };
  let normalizeUnit = unit;
  let altValue = null;
  let altUnit = null;
  const v = Number(value);
  const u = (unit || '').toString().trim().toLowerCase();
  switch (true) {
    case /(deg\s*c|celsius|°c|c$)/i.test(u):
      // Convert Celsius to Fahrenheit: F = C * 9/5 + 32
      // Target unit: °F
      normalizeUnit = '°C';
      altValue = roundToHundredths((v * 9) / 5 + 32);
      altUnit = '°F';
      break;
    case /(deg\s*f|fahrenheit|°f|f$)/i.test(u):
      // Convert Fahrenheit to Celsius: C = (F - 32) * 5/9
      // Target unit: °C
      normalizeUnit = '°F';
      altValue = roundToHundredths(((v - 32) * 5) / 9);
      altUnit = '°C';
      break;
    case /^(ft|feet?)$/.test(u):
      // Convert feet to inches: in = ft * 12
      // Target unit: in
      normalizeUnit = 'ft';
      altValue = roundToHundredths(v * 12);
      altUnit = 'in';
      break;
    case /^(in|inch|inches)$/.test(u):
      // Convert inches to feet: ft = in / 12
      // Target unit: ft
      normalizeUnit = 'in';
      altValue = roundToHundredths(v / 12);
      altUnit = 'ft';
      break;
    case /^(yd|yard|yards)$/.test(u):
      // Convert yards to meters: m = yd * 0.9144
      // Target unit: m
      normalizeUnit = 'yd';
      altValue = roundToHundredths(v * 0.9144);
      altUnit = 'm';
      break;
    case /(mi|mile|miles)$/.test(u):
      // Convert miles to kilometers: km = mi * 1.609344
      // Target unit: km
      normalizeUnit = 'mi';
      altValue = roundToHundredths(v * 1.609344);
      altUnit = 'km';
      break;
    case /^(m|meter|metre|meters|metres)$/.test(u):
      // Convert meters to feet: ft = m / 0.3048
      // Target unit: ft
      normalizeUnit = 'm';
      altValue = roundToHundredths(v / 0.3048);
      altUnit = 'ft';
      break;
    case /^(cm|centimeter|centimetre|centimeters|centimetres)$/.test(u):
      // Convert centimeters to inches: in = cm / 2.54
      // Target unit: in
      normalizeUnit = 'cm';
      altValue = roundToHundredths(v / 2.54);
      altUnit = 'in';
      break;
    case /^(km|kilometer|kilometre|kilometers|kilometres)$/.test(u):
      // Convert kilometers to miles: mi = km / 1.609344
      // Target unit: mi
      normalizeUnit = 'km';
      altValue = roundToHundredths(v / 1.609344);
      altUnit = 'mi';
      break;
    case /^(psi)$/.test(u):
      // Convert PSI to kilopascals: kPa = psi * 6.89475729
      // Target unit: kPa
      normalizeUnit = 'psi';
      altValue = roundToHundredths(v * 6.89475729);
      altUnit = 'kPa';
      break;
    case /^(kpa|kilopascal|kilopascals)$/.test(u):
      // Convert kilopascals to PSI: psi = kPa / 6.89475729
      // Target unit: psi
      normalizeUnit = 'kpa';
      altValue = roundToHundredths(v / 6.89475729);
      altUnit = 'psi';
      break;
    case /^cfs$/i.test(u) || /cubic\s*feet\s*per\s*second/i.test(u):
      // Cubic feet per second: display with superscript 3
      normalizeUnit = 'ft\u00B3/s';
      altValue = null;
      altUnit = null;
      break;
    default:
      normalizeUnit = unit;
      altValue = null;
      altUnit = null;
      break;
  }
  return {
    value: Number(value),
    unit: formatTemp(normalizeUnit),
    alt_value: altValue,
    alt_unit: altUnit,
  };
}

/**
 * [Funnction to easily round a number to the hundreths place]
 *
 * @param   {[number]}  num  [Any number with decimal point]
 *
 * @return  {[Number|any]}       [if not a number, return original object]
 */
export function roundToHundredths(num) {
  if (num == null || !isFinite(Number(num))) return num;
  return Math.round(num * 100) / 100;
}
